import React, { useState } from 'react';
import api from '../api/axiosConfig';

const ReportAnalytics = ({ agencies, getServiceTypeLabel, getDayName }) => {
  // State'ler
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth());
  const [reportAgencyId, setReportAgencyId] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState('agency');

  // Ay isimleri
  const MONTHS_TR = [
    "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"
  ];

  // Rapor verileri çekme
  const fetchReportAnalytics = async () => {
    setReportLoading(true);
    
    try {
      const response = await api.post('/api/admin/reports/monthly', {
        year: reportYear,
        month: reportMonth + 1, // API 1-12 arası bekliyor
        agency_id: reportAgencyId || null
      });
      
      if (response.data.success && response.data.data) {
        setReportData(response.data.data);
      } else {
        alert(response.data.message || 'Seçilen dönemde transfer bulunamadı.');
        setReportData(null);
      }
      
    } catch (error) {
      console.error('Rapor verileri yüklenirken hata:', error);
      alert('Rapor verileri yüklenirken bir hata oluştu: ' + (error.response?.data?.detail || error.message));
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  };

  // KPI Card bileşeni
  const KPICard = ({ icon, label, value }) => (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-2xl font-semibold mt-1">{value}</div>
        </div>
        <div className="h-10 w-10 flex items-center justify-center rounded-xl bg-gray-50">
          {icon}
        </div>
      </div>
    </div>
  );

  // Basit Bar Chart bileşeni
  const SimpleBarChart = ({ data, dataKey, xKey, title }) => (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className="space-y-2">
        {data.map((item, index) => {
          const maxValue = Math.max(...data.map(d => d[dataKey]));
          const percentage = maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0;
          
          return (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm text-gray-600 min-w-0 flex-1 truncate">{item[xKey]}</span>
              <div className="flex items-center ml-4">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium text-gray-900 w-8 text-right">{item[dataKey]}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Pie Chart bileşeni (basit)
  const SimplePieChart = ({ data, title }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.map((item, index) => {
            const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0';
            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2" 
                    style={{ backgroundColor: colors[index % colors.length] }}
                  ></div>
                  <span className="text-sm text-gray-600">{item.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">{item.value}</span>
                  <span className="text-xs text-gray-500 ml-1">({percentage}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header + Filtreler */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Aylık Rapor Analizi</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yıl</label>
            <select
              value={reportYear}
              onChange={(e) => setReportYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 6 }).map((_, i) => {
                const y = new Date().getFullYear() - 2 + i;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ay</label>
            <select
              value={reportMonth}
              onChange={(e) => setReportMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {MONTHS_TR.map((month, index) => (
                <option key={index} value={index}>{month}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acente (İsteğe Bağlı)</label>
            <select
              value={reportAgencyId}
              onChange={(e) => setReportAgencyId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tüm Acenteler</option>
              {agencies.map(agency => (
                <option key={agency.id} value={agency.id}>
                  {agency.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchReportAnalytics}
              disabled={reportLoading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {reportLoading ? 'Yükleniyor...' : 'Analiz Yap'}
            </button>
          </div>

          <div className="md:col-span-6 text-xs text-gray-500">
            Seçilen dönem: <strong>{MONTHS_TR[reportMonth]} {reportYear}</strong>
          </div>
        </div>
      </div>

      {/* Sonuçlar */}
      {reportData && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KPICard
              icon={<svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
              label="Toplam Transfer"
              value={reportData.summary.total_transfers}
            />
            <KPICard
              icon={<svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>}
              label="Toplam Yolcu"
              value={reportData.summary.total_passengers}
            />
            <KPICard
              icon={<svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>}
              label="Havalimanı Oranı"
              value={`${reportData.summary.airport_ratio}%`}
            />
            <KPICard
              icon={<svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
              label="Ort. Yolcu/Transfer"
              value={reportData.summary.avg_passengers_per_transfer}
            />
            <KPICard
              icon={<svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              label="Ort. Ön Bildirim (saat)"
              value={reportData.summary.avg_advance_booking_hours}
            />
            <KPICard
              icon={<svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
              label="Havalimanı Transferleri"
              value={reportData.summary.airport_transfers}
            />
          </div>

          {/* Grafikler */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SimplePieChart
              title="Servis Tipi Dağılımı"
              data={Object.entries(reportData.summary.service_distribution).map(([type, count]) => ({
                label: getServiceTypeLabel(type),
                value: count
              }))}
            />

            <SimpleBarChart
              title="Günlere Göre Dağılım"
              data={Object.entries(reportData.summary.day_distribution).map(([day, count]) => ({
                day: getDayName(day),
                count: count
              }))}
              dataKey="count"
              xKey="day"
            />

            <SimpleBarChart
              title="Saatlere Göre Dağılım (Top 10)"
              data={Object.entries(reportData.summary.hour_distribution)
                .map(([hour, count]) => ({ hour: `${hour}:00`, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10)}
              dataKey="count"
              xKey="hour"
            />

            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">En Çok Kullanılan Rotalar</h3>
              <div className="space-y-2">
                {reportData.summary.top_routes.slice(0, 8).map(([route, count], index) => (
                  <div key={index} className="flex items-center justify-between rounded-lg border p-2">
                    <div className="flex items-center gap-2 text-sm">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="truncate">{route}</span>
                    </div>
                    <div className="text-sm font-medium">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Acenteler / Şoförler / Plakalar Tabları */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button
                  onClick={() => setActiveReportTab('agency')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeReportTab === 'agency'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Acenteye Göre
                </button>
                <button
                  onClick={() => setActiveReportTab('driver')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeReportTab === 'driver'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Şoföre Göre
                </button>
                <button
                  onClick={() => setActiveReportTab('vehicle')}
                  className={`py-2 px-4 border-b-2 font-medium text-sm ${
                    activeReportTab === 'vehicle'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Plakaya Göre
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeReportTab === 'agency' && (
                <SimpleBarChart
                  title="Acenteye Göre İş Sayısı"
                  data={Object.entries(reportData.by_agency).map(([agency, stats]) => ({
                    agency: agency,
                    count: stats.transfer_count
                  }))}
                  dataKey="count"
                  xKey="agency"
                />
              )}

              {activeReportTab === 'driver' && (
                <SimpleBarChart
                  title="Şoföre Göre İş Sayısı"
                  data={Object.entries(reportData.by_driver).map(([driver, stats]) => ({
                    driver: driver,
                    count: stats.transfer_count
                  }))}
                  dataKey="count"
                  xKey="driver"
                />
              )}

              {activeReportTab === 'vehicle' && (
                <div className="space-y-6">
                  <SimpleBarChart
                    title="Plakaya Göre İş Sayısı"
                    data={Object.entries(reportData.by_vehicle).map(([plate, stats]) => ({
                      plate: plate,
                      count: stats.transfer_count
                    }))}
                    dataKey="count"
                    xKey="plate"
                  />

                  {/* Detaylı Plaka ve Rota Bilgileri */}
                  <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900">Plaka Bazında Rota Detayları</h3>
                      <p className="text-sm text-gray-500 mt-1">Her plaka hangi rotalara kaç kere gitmiş</p>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {Object.entries(reportData.by_vehicle).map(([plate, stats]) => (
                        <div key={plate} className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              </div>
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">{plate}</h4>
                                <p className="text-sm text-gray-500">
                                  {stats.transfer_count} Transfer • {stats.passenger_count} Yolcu • %{stats.completion_rate.toFixed(1)} Tamamlanma
                                </p>
                              </div>
                            </div>
                          </div>

                          {stats.top_routes && stats.top_routes.length > 0 && (
                            <div className="ml-13 space-y-2">
                              <div className="text-sm font-medium text-gray-700 mb-2">En Çok Kullanılan Rotalar:</div>
                              {stats.top_routes.slice(0, 5).map(([route, count], idx) => (
                                <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className="text-xs font-semibold text-gray-500 w-5">#{idx + 1}</span>
                                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span className="text-sm text-gray-700 truncate">{route}</span>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <div className="text-right">
                                      <span className="text-sm font-semibold text-gray-900">{count}</span>
                                      <span className="text-xs text-gray-500 ml-1">sefer</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Havalimanı İşleri Detay */}
          {reportData.airport_details && reportData.airport_details.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Havalimanı İşleri (Detay)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Şoför</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alış</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bırakış</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih/Saat</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pax</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tip</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yolcu</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TC/ID</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.airport_details.map((transfer) => (
                      <tr key={transfer.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{transfer.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.agency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.driver || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {transfer.pickup}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {transfer.dropoff}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.date} {transfer.time}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          {transfer.pax_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {getServiceTypeLabel(transfer.service_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.passenger_name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.passenger_id_no || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Veri yoksa gösterilecek */}
      {!reportData && !reportLoading && (
        <div className="bg-white p-12 rounded-lg shadow-sm text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Rapor Analizi</h3>
          <p className="text-gray-600 mb-4">Yukarıdaki filtreleri kullanarak bir dönem seçin ve "Analiz Yap" butonuna tıklayın.</p>
        </div>
      )}
    </div>
  );
};

export default ReportAnalytics;
